import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { markdownToSafeHtml } from "./RichDescriptionRenderer";
import "./RichDescriptionCellRenderer.scss";

const DEFAULT_EMPTY = "-";

/**
 * AG Grid cell renderer for the booking Description column.
 *
 * Renders the booking's `richDescription` markdown as inline sanitized HTML
 * (bold / italic / underline / links). Falls back to the plain `description`
 * field if `richDescription` is empty so previously created bookings without
 * markdown still display their description text.
 *
 * NOTE: `valueGetter` / `filterValueGetter` on the column should keep
 * returning plain text (via deriveShortDescription) so sort, quick filter,
 * "contains" filter and CSV/Excel export keep working on plain text.
 */
function RichDescriptionCellRenderer(params) {
  const data = params?.data;
  const richValue = data?.richDescription;
  const shortValue = data?.description;

  const html = useMemo(() => {
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return markdownToSafeHtml(richValue);
    }
    return "";
  }, [richValue]);

  if (html) {
    return (
      <span
        className="rich-description-cell"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  if (typeof shortValue === "string" && shortValue.trim().length > 0) {
    return <span className="rich-description-cell">{shortValue}</span>;
  }

  return <span className="rich-description-cell">{DEFAULT_EMPTY}</span>;
}

RichDescriptionCellRenderer.propTypes = {
  data: PropTypes.shape({
    richDescription: PropTypes.string,
    description: PropTypes.string,
  }),
};

RichDescriptionCellRenderer.defaultProps = {
  data: undefined,
};

export default RichDescriptionCellRenderer;
